/**
 * BrokenVideosBanner — teacher-facing alert card that lists every video whose
 * URL failed the automated health check (see backend/services/video_link_check.py).
 *
 * Pattern: appears above the videos list inside the Videos tab of the Lehrer-
 * Dashboard. Each row exposes a "Erneut prüfen" (recheck) button so the
 * teacher can revalidate immediately after they fix the URL upstream (Vimeo /
 * YouTube dashboard) without waiting for the next 6-hour scan.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { WarningCircle, ArrowClockwise, Spinner, VideoCamera } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrokenVideosBanner() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechecking, setRechecking] = useState({}); // video_id → bool

  const load = async () => {
    try {
      const res = await axios.get(`${API}/teacher/videos/broken`, { withCredentials: true });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // 403 for non-teacher OR the endpoint hasn't shipped yet → silently hide.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const recheck = async (vid) => {
    setRechecking(prev => ({ ...prev, [vid]: true }));
    try {
      const res = await axios.post(`${API}/teacher/videos/${vid}/recheck`, {}, { withCredentials: true });
      if (res.data?.link_broken) {
        toast.error(`Weiterhin offline (${res.data.link_last_status || res.data.link_last_error || 'Fehler'})`);
      } else {
        toast.success('Link ist wieder erreichbar');
        setItems(prev => prev.filter(v => v.video_id !== vid));
      }
    } catch (e) {
      toast.error('Prüfung fehlgeschlagen');
    } finally {
      setRechecking(prev => ({ ...prev, [vid]: false }));
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <div
      className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 sm:p-5 space-y-3"
      data-testid="broken-videos-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
          <WarningCircle size={22} weight="fill" color="white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-bold text-[#78350F] text-base sm:text-lg">
            {items.length === 1
              ? '1 Video-Link scheint offline'
              : `${items.length} Video-Links scheinen offline`}
          </h3>
          <p className="text-xs sm:text-sm text-amber-800/80 mt-0.5">
            Die automatische Prüfung konnte diese Videos nicht erreichen. Prüfe die
            URL bei YouTube/Vimeo und klicke dann auf „Erneut prüfen".
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {items.map(v => (
          <div
            key={v.video_id}
            className="bg-white rounded-xl p-3 flex items-center gap-3 border border-amber-200"
            data-testid={`broken-video-${v.video_id}`}
          >
            <VideoCamera size={20} weight="duotone" className="text-amber-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-[#292524] truncate">{v.title || '(ohne Titel)'}</p>
              <p className="text-[11px] text-[#78716C] truncate font-mono">{v.video_url}</p>
              {(v.link_last_status || v.link_last_error) && (
                <p className="text-[10px] text-amber-700 mt-0.5">
                  Letzte Prüfung: {v.link_last_status ? `HTTP ${v.link_last_status}` : `Fehler (${v.link_last_error})`}
                </p>
              )}
            </div>
            <button
              onClick={() => recheck(v.video_id)}
              disabled={rechecking[v.video_id]}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 disabled:opacity-60 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
              data-testid={`recheck-video-${v.video_id}`}
            >
              {rechecking[v.video_id]
                ? <Spinner size={14} className="animate-spin" />
                : <ArrowClockwise size={14} weight="bold" />}
              Erneut prüfen
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
